import { catchError, Observable, shareReplay, tap, throwError } from 'rxjs';

interface TimedRequestCacheEntry<T> {
  expiresAt: number;
  request: Observable<T>;
}

export class TimedRequestCache<T> {
  private readonly entries = new Map<string, TimedRequestCacheEntry<T>>();

  constructor(private readonly ttlMs = 10_000) {}

  get(key: string, requestFactory: () => Observable<T>): Observable<T> {
    const now = Date.now();
    const cached = this.entries.get(key);

    if (cached && cached.expiresAt > now) {
      return cached.request;
    }

    const entry = {
      expiresAt: Number.POSITIVE_INFINITY,
      request: undefined as unknown as Observable<T>,
    };

    entry.request = requestFactory().pipe(
      tap(() => {
        entry.expiresAt = Date.now() + this.ttlMs;
      }),
      catchError((error) => {
        if (this.entries.get(key) === entry) {
          this.entries.delete(key);
        }

        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.entries.set(key, entry);
    return entry.request;
  }

  invalidate(key: string): void {
    this.entries.delete(key);
  }

  clear(): void {
    this.entries.clear();
  }
}
